// h5ad-viewer/src/pages/CompareTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import H5ADUploader from "../components/H5ADUploader";
import SavedFileSelector from "../components/SavedFileSelector";
import H5ADReadPanel from "../components/H5ADReadPanel";
import Plot from "react-plotly.js";

const API_BASE = process.env.REACT_APP_API_BASE;

/** Compute per-gene mean across selected cells (X: rows=cells, cols=genes). */
function geneMeans(matrix /* number[][] */) {
  if (!matrix || matrix.length === 0) return [];
  const nRows = matrix.length;
  const nCols = matrix[0].length;
  const sums = new Float64Array(nCols);
  for (let r = 0; r < nRows; r++) {
    const row = matrix[r];
    for (let c = 0; c < nCols; c++) sums[c] += row[c];
  }
  const means = new Float64Array(nCols);
  for (let c = 0; c < nCols; c++) means[c] = sums[c] / Math.max(1, nRows);
  return Array.from(means);
}

/** Build a quick compare summary on intersecting genes. */
function buildCompareSummary(resultA, resultB, topK = 20) {
  if (!resultA || !resultB) return null;
  const genesA = resultA.genes || [];
  const genesB = resultB.genes || [];
  if (genesA.length === 0 || genesB.length === 0) return null;

  // Intersect genes
  const idxB = new Map();
  genesB.forEach((g, i) => idxB.set(g, i));
  const common = [];
  const aToCommon = [];
  const bToCommon = [];
  genesA.forEach((g, i) => {
    if (idxB.has(g)) {
      common.push(g);
      aToCommon.push(i);
      bToCommon.push(idxB.get(g));
    }
  });

  if (common.length === 0) {
    return { warning: "No overlapping genes between A and B selections." };
  }

  // Means
  const meanA = geneMeans(resultA.X).map((v, i) => v); // over genesA
  const meanB = geneMeans(resultB.X).map((v, i) => v); // over genesB

  // Extract aligned arrays by common genes
  const xA = aToCommon.map((i) => meanA[i]);
  const yB = bToCommon.map((i) => meanB[i]);

  // Pearson r
  const n = xA.length;
  const mean_x = xA.reduce((a, b) => a + b, 0) / n;
  const mean_y = yB.reduce((a, b) => a + b, 0) / n;
  let num = 0,
    denx = 0,
    deny = 0;
  for (let i = 0; i < n; i++) {
    const dx = xA[i] - mean_x;
    const dy = yB[i] - mean_y;
    num += dx * dy;
    denx += dx * dx;
    deny += dy * dy;
  }
  const pearson = denx > 0 && deny > 0 ? num / Math.sqrt(denx * deny) : 0;

  // Top-K by absolute difference (A_mean - B_mean on common genes)
  const diffs = xA.map((v, i) => v - yB[i]);
  const indices = Array.from({ length: n }, (_, i) => i);
  indices.sort((i, j) => Math.abs(diffs[j]) - Math.abs(diffs[i]));
  const top = indices.slice(0, topK).map((i) => ({
    gene: common[i],
    diff: diffs[i],
    meanA: xA[i],
    meanB: yB[i],
  }));

  return {
    nCellsA: resultA.cells?.length || 0,
    nCellsB: resultB.cells?.length || 0,
    nGenesA: genesA.length,
    nGenesB: genesB.length,
    nGenesCommon: common.length,
    pearson,
    scatter: { x: xA, y: yB }, // for plot
    topDiff: top, // table
  };
}

/** Subpanel wrapper to manage handle + metadata cache + read panel. */
function GroupQuery({
  label,
  metadataCache,
  setMetadataCache,
  onResult,
  resetTrigger = 0,
}) {
  const [handle, setHandle] = useState(null);
  const [resetSelectorCounter, setResetSelectorCounter] = useState(0);
  const [readStatus, setReadStatus] = useState("idle");
  const [readDuration, setReadDuration] = useState(null);

  const metadata = handle ? metadataCache[handle] : null;

  // uploader success
  const onUploadSuccess = (newHandle) => {
    setHandle(newHandle);
    setResetSelectorCounter((c) => c + 1);
    setReadStatus("idle");
    setReadDuration(null);
  };

  // saved file
  const onFileSelect = (filename) => {
    const newHandle = filename.replace(/\.h5ad$/, "");
    setHandle(newHandle);
    setReadStatus("idle");
    setReadDuration(null);
  };

  // fetch metadata if needed
  useEffect(() => {
    if (!handle) return;
    if (metadataCache[handle]) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/metadata/${handle}`);
        const data = await res.json();
        setMetadataCache((prev) => ({ ...prev, [handle]: data }));
      } catch (e) {
        console.error(`[${label}] failed to fetch metadata:`, e);
      }
    })();
  }, [handle, metadataCache, setMetadataCache, label]);

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">{label}</h3>
      <H5ADUploader onUploadSuccess={onUploadSuccess} />
      <SavedFileSelector
        onSelect={onFileSelect}
        resetTrigger={resetSelectorCounter + resetTrigger}
      />
      {handle && metadata && (
        <H5ADReadPanel
          handle={handle}
          metadata={metadata}
          onResult={onResult}
          setReadStatus={setReadStatus}
          setReadDuration={setReadDuration}
        />
      )}
      {!handle && (
        <div className="text-xs text-gray-500">Select or upload a .h5ad file.</div>
      )}
      {readDuration && (
        <div className="text-xs text-green-600">Loaded in {readDuration}s</div>
      )}
      {readStatus === "error" && (
        <div className="text-xs text-red-600">Query failed.</div>
      )}
    </div>
  );
}

export default function CompareTab() {
  // shared metadata cache keyed by handle (works for both groups)
  const [metadataCache, setMetadataCache] = useState({});

  const [resultA, setResultA] = useState(null);
  const [resultB, setResultB] = useState(null);

  const summary = useMemo(() => buildCompareSummary(resultA, resultB, 20), [resultA, resultB]);

  return (
    <div className="flex">
      {/* Left Panel: two groups */}
      <div className="w-1/4 pr-4 space-y-6">
        <GroupQuery
          label="Group A"
          metadataCache={metadataCache}
          setMetadataCache={setMetadataCache}
          onResult={setResultA}
        />
        <GroupQuery
          label="Group B"
          metadataCache={metadataCache}
          setMetadataCache={setMetadataCache}
          onResult={setResultB}
          resetTrigger={1} // separated reset stream
        />
      </div>

      {/* Right Panel: compare visualization */}
      <div className="w-3/4 bg-white rounded shadow p-4">
        {!resultA || !resultB ? (
          <div className="text-gray-600">
            Select files, build queries for both groups, then click <b>Query</b> in each group.
          </div>
        ) : summary?.warning ? (
          <div className="text-red-600">{summary.warning}</div>
        ) : (
          <>
            <div className="grid grid-cols-5 gap-3 mb-4">
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500">Cells (A)</div>
                <div className="text-xl font-bold">{summary.nCellsA}</div>
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500">Cells (B)</div>
                <div className="text-xl font-bold">{summary.nCellsB}</div>
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500">Genes (A)</div>
                <div className="text-xl font-bold">{summary.nGenesA}</div>
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500">Genes (B)</div>
                <div className="text-xl font-bold">{summary.nGenesB}</div>
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500">Common genes</div>
                <div className="text-xl font-bold">{summary.nGenesCommon}</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="border rounded p-3 col-span-1">
                <div className="text-xs text-gray-500 mb-1">Gene mean correlation</div>
                <div className="text-2xl font-bold">
                  {Number.isFinite(summary.pearson) ? summary.pearson.toFixed(3) : "NA"}
                </div>
              </div>
              <div className="col-span-2" />
            </div>

            <div className="mb-6">
              <Plot
                data={[
                  {
                    x: summary.scatter.x,
                    y: summary.scatter.y,
                    mode: "markers",
                    type: "scatter",
                  },
                ]}
                layout={{
                  title: "Gene-wise mean expression (A vs B) on common genes",
                  height: 420,
                  xaxis: { title: "Group A mean" },
                  yaxis: { title: "Group B mean" },
                }}
              />
            </div>

            <div>
              <h4 className="font-semibold mb-2">Top |meanA − meanB| genes (first 20)</h4>
              <div className="overflow-auto" style={{ maxHeight: 260 }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-1 pr-3">Gene</th>
                      <th className="py-1 pr-3">Δ = meanA − meanB</th>
                      <th className="py-1 pr-3">meanA</th>
                      <th className="py-1 pr-3">meanB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.topDiff.map((r, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-1 pr-3">{r.gene}</td>
                        <td className="py-1 pr-3">{r.diff.toFixed(4)}</td>
                        <td className="py-1 pr-3">{r.meanA.toFixed(4)}</td>
                        <td className="py-1 pr-3">{r.meanB.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
